// useFidelizacionCampana.ts
//
// Estado de la pantalla de campañas de fidelización:
//  - carga del Excel
//  - enriquecimiento (consulta de clientes) y envío de HSM con poll a /estado
//  - previsualización paginada con filtro por estado y búsqueda

import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  cancelarFidelizacion,
  eliminarFidelizacionLote,
  enriquecerFidelizacion,
  enviarFidelizacionHsm,
  getFidelizacionConfig,
  getFidelizacionContactos,
  getFidelizacionEstado,
  getFidelizacionError,
  getFidelizacionLote,
  getFidelizacionLotes,
  getFidelizacionPlantilla,
  subirFidelizacionArchivo,
  type FiltrosLotes,
} from "@/shared/actions/campanas/fidelizacion-campana.actions";
import type {
  FidelizacionConfig,
  FidelizacionContacto,
  FidelizacionEnvioPayload,
  FidelizacionLote,
  FidelizacionPage,
  FidelizacionProceso,
} from "../fidelizacion-campana.interface";

///* paginación y rango de fechas de la lista de campañas
export interface ConsultaLotes extends FiltrosLotes {
  pagina?: number;
  tamano?: number;
}

///* cada cuánto se pregunta el avance mientras hay un proceso corriendo
const POLL_MS = 2000;

export const useFidelizacionConfig = () =>
  useQuery<FidelizacionConfig>({
    queryKey: ["fidelizacion", "config"],
    queryFn: getFidelizacionConfig,
    staleTime: 5 * 60 * 1000,
  });

///* page y filtros van en el queryKey: al cambiarlos react-query vuelve a
///* pedir la pagina correcta en vez de mostrar la que ya estaba cacheada
export const useFidelizacionLotes = (
  { pagina = 0, tamano = 10, fechaDesde, fechaHasta }: ConsultaLotes = {},
  refrescar = false,
) =>
  useQuery<FidelizacionPage<FidelizacionLote>>({
    queryKey: ["fidelizacion", "lotes", { pagina, tamano, fechaDesde, fechaHasta }],
    queryFn: () => getFidelizacionLotes(pagina, tamano, { fechaDesde, fechaHasta }),
    refetchInterval: refrescar ? POLL_MS : false,
    refetchIntervalInBackground: true,
    ///* se conserva la pagina anterior mientras llega la nueva, para que la
    ///* lista no parpadee al cambiar de pagina o de filtro
    placeholderData: (previo: FidelizacionPage<FidelizacionLote> | undefined) => previo,
  });

///* el lote en pantalla se pide por id y no de la lista: asi sigue visible al
///* cambiar de pagina o de filtro de fecha, aunque no este en la pagina visible
export const useFidelizacionLote = (loteId: number | null, refrescar = false) =>
  useQuery<FidelizacionLote>({
    queryKey: ["fidelizacion", "lote", loteId],
    queryFn: () => getFidelizacionLote(loteId!),
    enabled: !!loteId,
    refetchInterval: refrescar ? POLL_MS : false,
    refetchIntervalInBackground: true,
  });

export const useSubirFidelizacion = (onSuccess?: (loteId: number) => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => subirFidelizacionArchivo(file),
    onSuccess: (response) => {
      toast.success(
        response?.message ?? `Archivo cargado con ${response?.lote?.total_filas ?? 0} filas`,
      );
      queryClient.invalidateQueries({ queryKey: ["fidelizacion"] });
      if (response?.lote?.id) onSuccess?.(response.lote.id);
    },
    onError: (error) => {
      toast.error(getFidelizacionError(error, "No se pudo cargar el archivo."));
    },
  });
};

///* descarga el XLSX con el formato de columnas que espera el microservicio
export const useDescargarPlantillaFidelizacion = () => {
  const [descargando, setDescargando] = useState(false);

  const descargar = useCallback(async () => {
    setDescargando(true);
    try {
      const blob = await getFidelizacionPlantilla();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "plantilla_fidelizacion.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getFidelizacionError(error, "No se pudo descargar la plantilla."));
    } finally {
      setDescargando(false);
    }
  }, []);

  return { descargando, descargar };
};

export const useEnriquecerFidelizacion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (loteId: number) => enriquecerFidelizacion(loteId),
    onSuccess: () => {
      toast.info("Consultando datos de los clientes.");
      queryClient.invalidateQueries({ queryKey: ["fidelizacion"] });
    },
    onError: (error) => {
      toast.error(getFidelizacionError(error, "No se pudo iniciar la consulta de clientes."));
    },
  });
};

export const useEnviarFidelizacionHsm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ loteId, payload }: { loteId: number; payload: FidelizacionEnvioPayload }) =>
      enviarFidelizacionHsm(loteId, payload),
    onSuccess: () => {
      toast.info("Enviando la campaña.");
      queryClient.invalidateQueries({ queryKey: ["fidelizacion"] });
    },
    onError: (error) => {
      toast.error(getFidelizacionError(error, "No se pudo iniciar el envío."));
    },
  });
};

export const useCancelarFidelizacion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (loteId: number) => cancelarFidelizacion(loteId),
    onSuccess: () => {
      toast.info("Cancelación solicitada.");
      queryClient.invalidateQueries({ queryKey: ["fidelizacion"] });
    },
    onError: (error) => {
      toast.error(getFidelizacionError(error, "No se pudo cancelar."));
    },
  });
};

export const useEliminarFidelizacion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (loteId: number) => eliminarFidelizacionLote(loteId),
    onSuccess: () => {
      toast.success("Campaña eliminada.");
      queryClient.invalidateQueries({ queryKey: ["fidelizacion"] });
    },
    onError: (error) => {
      toast.error(getFidelizacionError(error, "No se pudo eliminar el lote."));
    },
  });
};

///* sigue el proceso de un lote hasta que termina. Es una query para que el
///* poll arranque en cuanto se invalida (al pulsar "Consultar datos" o
///* "Enviar"), no solo al cambiar de lote, y se detenga solo al terminar.
export const useFidelizacionProceso = (loteId: number | null) =>
  useQuery<FidelizacionProceso>({
    queryKey: ["fidelizacion", "proceso", loteId],
    queryFn: () => getFidelizacionEstado(loteId!),
    enabled: !!loteId,
    refetchInterval: (query) =>
      query.state.data?.estado === "CORRIENDO" ? POLL_MS : false,
    refetchIntervalInBackground: true,
    retry: 2,
  });

///* previsualización con paginación del servidor
export const useFidelizacionContactos = (
  loteId: number | null,
  page: number,
  size: number,
  estado: string,
  busqueda: string,
  refrescar = false,
) =>
  useQuery({
    queryKey: ["fidelizacion", "contactos", loteId, page, size, estado, busqueda],
    queryFn: () =>
      getFidelizacionContactos(loteId!, page, size, estado || undefined, busqueda || undefined),
    enabled: !!loteId,
    //* mantiene la tabla visible mientras llega la página siguiente
    placeholderData: (previous) => previous,
    //* durante la consulta o el envío la tabla va cambiando fila a fila
    refetchInterval: refrescar ? POLL_MS : false,
    refetchIntervalInBackground: true,
  });

///* Un contacto es contactable si se le puede mandar el mensaje: eso exige un
///* telefono. Cuando no lo es, el motivo importa porque dice que hay que corregir
///* en el sistema de origen, asi que la etiqueta lo incluye.
export const ESTADO_CONTACTO_LABEL: Record<string, string> = {
  PENDIENTE: "Sin consultar",
  CONTACTABLE: "Contactable",
  SIN_TELEFONO: "No contactable · sin teléfono",
  NO_ENCONTRADO: "No contactable · no está registrado",
  ERROR: "No contactable · error al consultar",
};

export const ESTADO_LOTE_LABEL: Record<string, string> = {
  CARGADO: "Cargado",
  ENRIQUECIDO: "Listo para enviar",
  ENVIANDO: "Enviando",
  ENVIADO: "Enviado",
  ERROR: "Error",
};

///* color del chip según el estado
export const estadoColor = (
  estado: string,
): "default" | "success" | "warning" | "error" | "info" => {
  switch (estado) {
    case "CONTACTABLE":
      return "success";
    case "ENVIADO":
    case "COMPLETADO":
      return "info";
    ///* amber = el dato esta incompleto y hay que actualizarlo en el origen
    case "SIN_TELEFONO":
    case "NO_ENCONTRADO":
      return "warning";
    ///* rojo = la consulta fallo y hay que reintentarla
    case "ERROR":
      return "error";
    default:
      return "default";
  }
};

export type { FidelizacionContacto };
